// src/pages/BlogPost.js
import React from "react";
import { Helmet } from "react-helmet-async";
import tw from "twin.macro";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import Header from "components/headers/light";
import Footer from "components/footers/SimpleFiveColumn";

// --- Load MDX posts (CRA/Webpack) ---
const mdxContext = require.context("../blogs", false, /\.mdx$/);

const blogPosts = mdxContext.keys().map((key) => {
  const mod = mdxContext(key);
  const Component = mod.default || mod;
  const frontmatter = mod.frontmatter || {};
  const slugFromFile = key.replace("./", "").replace(/\.mdx$/, "");
  const slug = frontmatter.slug || slugFromFile;

  return {
    slug,
    component: Component,
    frontmatter,
  };
});





// content wrapper is above blobs
// p-8 + overflow-hidden matches the wrapper every other page uses. Header and
// footer are built for it: the footer bleeds edge-to-edge with -mx-8, which
// without this padding overflowed the viewport by exactly 2rem (32px).
const Page = tw.div`min-h-screen flex flex-col relative p-8 overflow-hidden`;
const Main = styled.main`
  ${tw`flex-1 max-w-4xl mx-auto p-6`}
  position: relative;
  z-index: 1;
`;

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <Page>
        <Header />
        <Main>
          <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-4">
            Sorry, the blog post you’re looking for doesn’t exist.
          </p>
          <Link to="/blog" className="text-primary-600 underline">
            Back to Blog
          </Link>
        </Main>
        <Footer />
      </Page>
    );
  }

  const PostComponent = post.component;
  const fm = post.frontmatter || {};

  return (
    <Page>
      <Helmet>
        <title>{fm.title ? `${fm.title} | EthosHub Blog` : "EthosHub Blog"}</title>
        <meta name="description" content={fm.description || "Read the latest insights from EthosHub on workplace reporting, HR compliance, and building safer workplace cultures."} />
        <link rel="canonical" href={`https://www.ethoshub.io/blog/${slug}`} />
        <meta property="og:title" content={fm.title ? `${fm.title} | EthosHub Blog` : "EthosHub Blog"} />
        <meta property="og:description" content={fm.description || ""} />
        <meta property="og:url" content={`https://www.ethoshub.io/blog/${slug}`} />
        {fm.image && <meta property="og:image" content={fm.image} />}
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Fixed background blobs */}

      <Header />
      <Main>
        {/* Optional banner image from frontmatter */}
        {fm.image && (
          <div className="mb-8">
            <img
              src={fm.image}
              alt={fm.title || "Blog banner"}
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        )}

        <article className="prose prose-lg max-w-none">
          <PostComponent />
        </article>
      </Main>
      <Footer />
    </Page>
  );
}